import { useState, useEffect, useMemo } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import { Loader2, AlertCircle } from 'lucide-react'
import { useFileIO } from '@papan/files/client/file-io'
import { useOnlineRecovery } from '@papan/realtime/client'
import { LARGE_CSV_ROUTING_ENABLED, isLargeCsv } from '@papan/types'
import { DataGridViewerInner, parseCsv as parseCsvGrid } from './DataGridViewer'
import { XlsxViewer } from './XlsxViewer'
import { LargeCsvGrid } from './LargeCsvGrid.js'
import { errorText } from '@papan/api-client'

interface CsvViewerProps {
  fileId: string
  fileName?: string
  versionId?: string
  /** The size of the content shown (the version's, in a version preview), when the surface knows
   *  it: over the large-CSV threshold the View mode is the windowed grid (#981). */
  size?: number | null
  /** Overrides LARGE_CSV_ROUTING_ENABLED (tests). */
  largeCsvRouting?: boolean
  /** Shared view mode from parent: 'preview' = View (AG Grid), 'source' = Edit (Univer) */
  viewMode?: 'preview' | 'source'
  onViewModeChange?: (mode: 'preview' | 'source') => void
  viewSync?: Record<string, Record<string, unknown>>
  viewSyncId?: string
  viewSyncTrigger?: number
}

/**
 * CSV version preview — View mode shows AG Grid, Edit mode shows read-only Univer.
 * Maps to the live editor's View/Edit toggle.
 */
export function CsvViewer({ fileId, fileName, versionId, viewMode, size, largeCsvRouting }: CsvViewerProps) {
  // 'preview' = View (AG Grid), 'source' = Edit (Univer)
  const mode = viewMode ?? 'preview'
  // #981: over the threshold the whole file is never loaded — the View mode reads it a window at a
  // time, and the Edit mode (Univer, which needs every row) shows the same windowed grid.
  if ((largeCsvRouting ?? LARGE_CSV_ROUTING_ENABLED) && isLargeCsv(fileName ?? '', size ?? null)) {
    return <LargeCsvGrid fileId={fileId} fileName={fileName} versionId={versionId || undefined} readOnly />
  }

  if (mode === 'source') {
    // Edit mode: use the same XLSX/CSV preview via Univer in read-only mode
    return (
      <XlsxViewer
        fileId={fileId}
        fileName={fileName}
        versionId={versionId || ''}
      />
    )
  }

  // View mode: AG Grid
  return (
    <CsvGridPreview fileId={fileId} fileName={fileName} versionId={versionId} />
  )
}

/** AG Grid view for CSV version preview */
function CsvGridPreview({ fileId, fileName, versionId }: { fileId: string; fileName?: string; versionId?: string }) {
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // #1316: a dropped answer (its session ended) is asked again as the session now here — never shown as an error
  // Review round 2: in the share view an end that MOVES the share session leaves the ended sid in use until the new
  // session lands — the re-ask waits for it there (`useDroppedLoadReask`), never asking as the ended session
  const { reask, begin } = useDroppedLoadReask()
  const recovery = useOnlineRecovery()
  const fileIO = useFileIO()

  useEffect(() => {
    const asked = begin()   // the share session this load goes out as
    setLoading(true)
    setError(null)
    fileIO.loadContent(fileId, versionId)
      .then(blob => blob.text())
      .then(text => {
        setContent(text)
        setLoading(false)
      })
      .catch(err => {
        const text = errorText(err, 'Failed to load CSV')
        if (text === null) { asked.dropped(); return }
        setError(text)
        setLoading(false)
      })
  }, [fileId, versionId, recovery, fileIO, reask, begin])

  const gridData = useMemo(() => {
    if (!content) return { columns: [], rows: [] as Record<string, unknown>[] }
    const result = parseCsvGrid(content)
    const rows = result.rows as Record<string, unknown>[]
    rows.forEach((r, i) => { r.__rowIdx = i })
    return { columns: result.columns, rows }
  }, [content])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="animate-spin themed-text-muted" size={24} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <div className="text-center">
          <AlertCircle size={32} className="mx-auto mb-2 text-red-400 opacity-60" />
          <p className="text-sm">Failed to load CSV: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <DataGridViewerInner
      fileId={fileId}
      fileName={fileName}
      previewVersionId={versionId || 'current'}
      columns={gridData.columns}
      rows={gridData.rows}
      loading={false}
      error={null}
    />
  )
}
